/**
 * Pure helpers for simulation results charts (Performance Trends).
 * Shared by Results page and Dashboard insights.
 *
 * @module simulationResultsChartData
 */

import type { SimulationResult } from '@/types/simulation-results';
import { getModuleAnalysisDisplayInfo } from '@/lib/analysisStatus';

export const SIMULATION_MODULE_COLORS = {
  reading: 'hsl(180, 45%, 50%)',
  writing: 'hsl(145, 60%, 45%)',
  listening: 'hsl(210, 70%, 55%)',
  speaking: 'hsl(340, 70%, 55%)',
} as const;

export type SimulationModuleKey = keyof typeof SIMULATION_MODULE_COLORS;

export const SIMULATION_MODULE_KEYS: readonly SimulationModuleKey[] = [
  'reading',
  'writing',
  'listening',
  'speaking',
];

export type ChartDataPoint = {
  /** Stable sort key: local calendar day YYYY-MM-DD */
  readonly dateKey: string;
  /** Display label for x-axis (e.g. "Apr 23") */
  readonly date: string;
  /** Number of simulation sessions on this calendar day */
  readonly attemptCount: number;
} & Partial<Record<SimulationModuleKey, number>>;

export type LatestModuleProgressStatus = 'graded' | 'grading' | 'failed' | 'none';

export interface LatestModuleProgress {
  readonly module: SimulationModuleKey;
  readonly status: LatestModuleProgressStatus;
  readonly percentage: number | null;
  readonly date: string | null;
  readonly passed: boolean | null;
  /** Session id for the latest attempt of this module (AC4 review clickthrough). */
  readonly sessionId: string | null;
}

export interface ModuleReviewTarget {
  readonly sessionId: string;
  readonly module: SimulationModuleKey;
}

/** Local calendar day key for bucketing chart points. */
export function getLocalDayKey(isoTimestamp: string): string {
  const d = new Date(isoTimestamp);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Human-readable x-axis label matching prior chart formatting. */
export function formatChartDayLabel(isoTimestamp: string): string {
  return new Date(isoTimestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Modules that appear in at least one simulation session (B7: omit never-attempted from charts). */
export function getAttemptedModuleKeys(results: SimulationResult[]): Set<SimulationModuleKey> {
  const attempted = new Set<SimulationModuleKey>();
  results.forEach((result) => {
    result.modules.forEach((moduleResult) => {
      const key = moduleResult.module as SimulationModuleKey;
      if (SIMULATION_MODULE_KEYS.includes(key)) {
        attempted.add(key);
      }
    });
  });
  return attempted;
}

export function calculateAverageScores(results: SimulationResult[]) {
  if (results.length === 0) return null;

  const totals: Record<SimulationModuleKey, number> = {
    reading: 0,
    writing: 0,
    listening: 0,
    speaking: 0,
  };
  const counts: Record<SimulationModuleKey, number> = {
    reading: 0,
    writing: 0,
    listening: 0,
    speaking: 0,
  };

  results.forEach((result) => {
    result.modules.forEach((module) => {
      const key = module.module as SimulationModuleKey;
      if (module.is_graded && SIMULATION_MODULE_KEYS.includes(key)) {
        totals[key] += module.percentage;
        counts[key] += 1;
      }
    });
  });

  return {
    reading: counts.reading > 0 ? Math.round(totals.reading / counts.reading) : undefined,
    writing: counts.writing > 0 ? Math.round(totals.writing / counts.writing) : undefined,
    listening: counts.listening > 0 ? Math.round(totals.listening / counts.listening) : undefined,
    speaking: counts.speaking > 0 ? Math.round(totals.speaking / counts.speaking) : undefined,
  };
}

export function calculateLatestModuleProgress(results: SimulationResult[]): LatestModuleProgress[] {
  const latest: Record<SimulationModuleKey, LatestModuleProgress> = {
    reading: { module: 'reading', status: 'none', percentage: null, date: null, passed: null, sessionId: null },
    writing: { module: 'writing', status: 'none', percentage: null, date: null, passed: null, sessionId: null },
    listening: { module: 'listening', status: 'none', percentage: null, date: null, passed: null, sessionId: null },
    speaking: { module: 'speaking', status: 'none', percentage: null, date: null, passed: null, sessionId: null },
  };

  const newestFirst = results
    .slice()
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  newestFirst.forEach((result) => {
    result.modules.forEach((moduleResult) => {
      const moduleKey = moduleResult.module as SimulationModuleKey;
      if (!(moduleKey in latest) || latest[moduleKey].status !== 'none') {
        return;
      }

      const analysisInfo = getModuleAnalysisDisplayInfo(moduleResult);

      latest[moduleKey] = {
        module: moduleKey,
        status: moduleResult.is_graded
          ? analysisInfo.state === 'graded'
            ? 'graded'
            : analysisInfo.state === 'failed'
              ? 'failed'
              : 'grading'
          : moduleResult.analysis_status === 'failed'
            ? 'failed'
            : 'grading',
        percentage: moduleResult.is_graded ? moduleResult.percentage : null,
        date: result.date,
        passed: moduleResult.is_graded ? moduleResult.passed : null,
        sessionId: result.id,
      };
    });
  });

  return SIMULATION_MODULE_KEYS.map((moduleKey) => latest[moduleKey]);
}

/** Latest graded attempt per module for AC4 chart/KPI clickthrough. */
export function getLatestReviewTargetByModule(
  results: SimulationResult[],
): Record<SimulationModuleKey, ModuleReviewTarget | null> {
  const targets = Object.fromEntries(
    SIMULATION_MODULE_KEYS.map((key) => [key, null]),
  ) as Record<SimulationModuleKey, ModuleReviewTarget | null>;

  const newestFirst = results
    .slice()
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  for (const result of newestFirst) {
    for (const moduleResult of result.modules) {
      const moduleKey = moduleResult.module as SimulationModuleKey;
      if (!SIMULATION_MODULE_KEYS.includes(moduleKey) || targets[moduleKey] !== null) {
        continue;
      }
      if (getModuleAnalysisDisplayInfo(moduleResult).state === 'graded') {
        targets[moduleKey] = { sessionId: result.id, module: moduleKey };
      }
    }
  }

  return targets;
}

/**
 * Builds one chart row per calendar day. Multiple simulations on the same day are
 * merged using the latest graded score per module (supports module-only runs).
 */
type DayBucket = {
  dateKey: string;
  date: string;
  attemptCount: number;
} & Partial<Record<SimulationModuleKey, number>>;

export function processChartData(results: SimulationResult[]): ChartDataPoint[] {
  if (results.length === 0) return [];

  const sorted = [...results].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );

  const byDay = new Map<string, DayBucket>();

  for (const result of sorted) {
    const dateKey = getLocalDayKey(result.date);
    const row =
      byDay.get(dateKey) ??
      ({
        dateKey,
        date: formatChartDayLabel(result.date),
        attemptCount: 0,
      } satisfies DayBucket);

    row.attemptCount += 1;

    for (const mod of result.modules) {
      const key = mod.module as SimulationModuleKey;
      if (mod.is_graded && SIMULATION_MODULE_KEYS.includes(key)) {
        row[key] = mod.percentage;
      }
    }

    byDay.set(dateKey, row);
  }

  return [...byDay.values()].sort((a, b) => a.dateKey.localeCompare(b.dateKey));
}
